"use client"

/**
 * SIB Player — Sound in Background
 * Persistent floating audio player that lives at the bottom of the screen.
 * Plays independently of video/script playback.
 */

import { useEffect, useRef, useState, useCallback } from "react"
import { useSibStore } from "@/lib/sib-store"
import { Slider } from "@/components/ui/slider"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Repeat,
  Repeat1,
  ChevronDown,
  ChevronUp,
  ListMusic,
  X,
  Music,
} from "@/lib/icons"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useTranslations } from "next-intl"

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00"
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, "0")}`
}

export function SibPlayer() {
  const t = useTranslations("sibPlayer")
  const {
    isPlaying,
    currentTrack,
    volume,
    isMuted,
    repeatMode,
    isMinimized,
    isVisible,
    queue,
    queueIndex,
    pause,
    resume,
    stop,
    next,
    prev,
    setVolume,
    setMuted,
    setCurrentTime,
    setRepeatMode,
    setMinimized,
    setVisible,
    removeFromQueue,
    playFromQueue,
  } = useSibStore()

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [showQueue, setShowQueue] = useState(false)
  const [isDraggingProgress, setIsDraggingProgress] = useState(false)
  const [localTime, setLocalTime] = useState(0)

  // Create audio element once
  useEffect(() => {
    if (typeof window === "undefined") return
    audioRef.current = new Audio()
    audioRef.current.volume = volume
    audioRef.current.muted = isMuted
    return () => {
      audioRef.current?.pause()
      audioRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync track changes
  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !currentTrack) return
    if (audio.src !== currentTrack.audioUrl) {
      audio.src = currentTrack.audioUrl
      audio.load()
    }
    if (isPlaying) {
      audio.play().catch((err) => {
        console.warn("[SIB] Autoplay blocked:", err)
        pause()
      })
    }
  }, [currentTrack, isPlaying, pause])

  // Sync play/pause
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    if (isPlaying) {
      audio.play().catch(() => pause())
    } else {
      audio.pause()
    }
  }, [isPlaying, pause])

  // Sync volume/mute
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.volume = volume
    audio.muted = isMuted
  }, [volume, isMuted])

  // Time update + ended
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    const onTimeUpdate = () => {
      if (!isDraggingProgress) {
        setLocalTime(audio.currentTime)
        setCurrentTime(audio.currentTime)
      }
    }
    const onEnded = () => {
      if (repeatMode === "one") {
        audio.currentTime = 0
        audio.play().catch(() => {})
      } else {
        next()
      }
    }
    audio.addEventListener("timeupdate", onTimeUpdate)
    audio.addEventListener("ended", onEnded)
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate)
      audio.removeEventListener("ended", onEnded)
    }
  }, [isDraggingProgress, repeatMode, next, setCurrentTime])

  const handleSeek = useCallback(
    (value: number[]) => {
      const audio = audioRef.current
      if (!audio || !currentTrack) return
      const newTime = value[0] ?? 0
      setLocalTime(newTime)
      audio.currentTime = newTime
      setCurrentTime(newTime)
    },
    [currentTrack, setCurrentTime],
  )

  const handleVolumeChange = useCallback(
    (value: number[]) => setVolume(value[0] ?? 0.7),
    [setVolume],
  )

  const cycleRepeat = useCallback(() => {
    const modes = ["none", "all", "one"] as const
    const current = modes.indexOf(repeatMode)
    setRepeatMode(modes[(current + 1) % modes.length] ?? "none")
  }, [repeatMode, setRepeatMode])

  if (!isVisible || !currentTrack) return null

  const duration = currentTrack.duration || 1
  const progress = (localTime / duration) * 100

  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 z-[60] bg-black/95 border-t border-purple-900/50 backdrop-blur-md",
        "transition-all duration-300 ease-in-out",
        isMinimized ? "h-12" : showQueue ? "h-72" : "h-20",
      )}
    >
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/10">
        <div className="h-full bg-purple-500 transition-all duration-100" style={{ width: `${progress}%` }} />
      </div>

      {/* Minimized bar */}
      {isMinimized ? (
        <div className="flex items-center h-12 px-4 gap-3">
          <Music className="h-4 w-4 text-purple-400 shrink-0" />
          <span className="text-xs text-white/70 truncate flex-1">{currentTrack.title}</span>
          <span className="text-xs text-white/40">{currentTrack.artist}</span>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-white/70 hover:text-white"
            onClick={() => (isPlaying ? pause() : resume())}>
            {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-white/40 hover:text-white"
            onClick={() => setMinimized(false)}>
            <ChevronUp className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <>
          {/* Main player row */}
          <div className="flex items-center h-20 px-4 gap-3">
            {/* Track info */}
            <div className="flex items-center gap-2 w-48 shrink-0 min-w-0">
              {currentTrack.artistAvatar ? (
                <img src={currentTrack.artistAvatar} alt={currentTrack.artist}
                  className="h-9 w-9 rounded-full object-cover shrink-0 border border-purple-800" />
              ) : (
                <div className="h-9 w-9 rounded-full bg-purple-900/60 flex items-center justify-center shrink-0">
                  <Music className="h-4 w-4 text-purple-400" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-xs font-medium text-white truncate leading-tight">{currentTrack.title}</p>
                <p className="text-xs text-white/50 truncate">{currentTrack.artist}</p>
              </div>
            </div>

            {/* Center controls + seek */}
            <div className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7 text-white/50 hover:text-white" onClick={prev}>
                  <SkipBack className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:text-purple-300"
                  onClick={() => (isPlaying ? pause() : resume())}>
                  {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-white/50 hover:text-white" onClick={next}>
                  <SkipForward className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon"
                  className={cn("h-7 w-7", repeatMode !== "none" ? "text-purple-400" : "text-white/30 hover:text-white/60")}
                  onClick={cycleRepeat} title={`Repeat: ${repeatMode}`}>
                  {repeatMode === "one" ? <Repeat1 className="h-3.5 w-3.5" /> : <Repeat className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <div className="flex items-center gap-2 w-full max-w-sm">
                <span className="text-[10px] text-white/40 w-8 text-right shrink-0">{formatTime(localTime)}</span>
                <Slider value={[localTime]} min={0} max={duration} step={1}
                  onValueChange={handleSeek}
                  onPointerDown={() => setIsDraggingProgress(true)}
                  onPointerUp={() => setIsDraggingProgress(false)}
                  className="flex-1 [&_[role=slider]]:h-3 [&_[role=slider]]:w-3 [&_[role=slider]]:bg-purple-400" />
                <span className="text-[10px] text-white/40 w-8 shrink-0">{formatTime(duration)}</span>
              </div>
            </div>

            {/* Right controls */}
            <div className="flex items-center gap-1 w-40 justify-end shrink-0">
              <Button variant="ghost" size="icon" className="h-7 w-7 text-white/50 hover:text-white"
                onClick={() => setMuted(!isMuted)}>
                {isMuted || volume === 0 ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
              </Button>
              <Slider value={[isMuted ? 0 : volume]} min={0} max={1} step={0.01}
                onValueChange={handleVolumeChange}
                className="w-20 [&_[role=slider]]:h-3 [&_[role=slider]]:w-3 [&_[role=slider]]:bg-white/70" />
              <Button variant="ghost" size="icon"
                className={cn("h-7 w-7", showQueue ? "text-purple-400" : "text-white/40 hover:text-white")}
                onClick={() => setShowQueue(!showQueue)} title={t("queue")}>
                <ListMusic className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-white/40 hover:text-white"
                onClick={() => setMinimized(true)}>
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-white/30 hover:text-red-400"
                onClick={() => { stop(); setVisible(false) }}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Queue panel */}
          {showQueue && (
            <div className="border-t border-white/10 h-52">
              <div className="flex items-center justify-between px-4 py-1.5">
                <span className="text-xs text-white/50 font-medium">{t("queueLength", { length: queue.length })}</span>
              </div>
              <ScrollArea className="h-40 px-2">
                {queue.length === 0 ? (
                  <p className="text-xs text-white/30 text-center py-4">{t("queueIsEmpty")}</p>
                ) : (
                  queue.map((track, i) => (
                    <div key={`${track.id}-${i}`}
                      className={cn(
                        "flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer group",
                        i === queueIndex ? "bg-purple-900/40 text-white" : "text-white/60 hover:bg-white/5 hover:text-white",
                      )}
                      onClick={() => playFromQueue(i)}>
                      {i === queueIndex && isPlaying ? (
                        <div className="flex gap-0.5 items-end h-3 shrink-0">
                          <span className="w-0.5 bg-purple-400 animate-[bounce_0.6s_ease-in-out_infinite]" style={{ height: "60%" }} />
                          <span className="w-0.5 bg-purple-400 animate-[bounce_0.6s_ease-in-out_infinite_0.1s]" style={{ height: "100%" }} />
                          <span className="w-0.5 bg-purple-400 animate-[bounce_0.6s_ease-in-out_infinite_0.2s]" style={{ height: "40%" }} />
                        </div>
                      ) : (
                        <span className="text-[10px] text-white/30 w-4 text-center shrink-0">{i + 1}</span>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs truncate">{track.title}</p>
                        <p className="text-[10px] text-white/40 truncate">{track.artist}</p>
                      </div>
                      <span className="text-[10px] text-white/30 shrink-0">{formatTime(track.duration)}</span>
                      <Button variant="ghost" size="icon"
                        className="h-5 w-5 opacity-0 group-hover:opacity-100 text-white/40 hover:text-red-400 shrink-0"
                        onClick={(e) => { e.stopPropagation(); removeFromQueue(i) }}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))
                )}
              </ScrollArea>
            </div>
          )}
        </>
      )}
    </div>
  )
}
